/**
 * Error Boundary Component
 * Catches unhandled errors and resets auth state to recover gracefully.
 */
import { Component, ReactNode } from 'react';
import { signOut } from 'aws-amplify/auth';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isResetting: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isResetting: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true, isResetting: false };
  }

  componentDidCatch(error: Error) {
    console.error('ErrorBoundary caught an error:', error);
  }

  handleReset = async () => {
    this.setState({ isResetting: true });
    try {
      await signOut();
    } catch {
      localStorage.clear();
      sessionStorage.clear();
    }
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              An unexpected error occurred. Click below to sign out and start fresh.
            </p>
            <button
              onClick={this.handleReset}
              disabled={this.state.isResetting}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {this.state.isResetting ? 'Resetting...' : 'Sign Out & Restart'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
